import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";

import { toast } from "sonner";

import type { Studio } from "@/types";

import { useAuth } from "./AuthContext";

import { apiClient, setActiveStudioId as setApiStudioId } from "@/lib/api-client";

import { getApiErrorMessage } from "@/lib/api-notify";

import {

  canUseAllStudiosScope,

  isStudioContextApiErrorCode,

  normalizeStudioScopeForUser,

} from "@/lib/studio-scope";



const STORAGE_KEY = "oktogo_active_studio";



interface StudioContextType {

  activeStudio: Studio | null;

  activeStudioId: string;

  isAllStudios: boolean;

  setActiveStudioId: (id: string) => void;

}



const StudioContext = createContext<StudioContextType | undefined>(undefined);



function readStoredStudioId(): string {

  try {

    return localStorage.getItem(STORAGE_KEY) ?? "all";

  } catch {

    return "all";

  }

}



function persistStudioId(id: string): void {

  try {

    localStorage.setItem(STORAGE_KEY, id);

  } catch {

    /* ignore */

  }

}



export function StudioProvider({ children }: { children: React.ReactNode }) {

  const { t } = useTranslation();

  const { user, serverCurrentStudioId } = useAuth();

  const [studioId, setStudioId] = useState<string>(() => {

    const id = readStoredStudioId();

    setApiStudioId(id);

    return id;

  });

  const studioIdRef = useRef(studioId);

  studioIdRef.current = studioId;

  const hasSyncedServerRef = useRef(false);



  const applyStudioScope = useCallback((id: string, options?: { persist?: boolean; syncServer?: boolean }) => {

    const { persist = true, syncServer = false } = options ?? {};

    setStudioId(id);

    if (persist) persistStudioId(id);

    setApiStudioId(id);

    if (syncServer) {

      void apiClient<{ currentStudioId: string }>("/users/me/studio-context", {

        method: "PUT",

        body: JSON.stringify({ studioId: id }),

        skipAutoRedirect: true,

      } as Parameters<typeof apiClient>[1]).catch(() => {});

    }

  }, []);



  // FE-13: reject ineligible "all" and unknown studio ids once user studios are known

  useEffect(() => {

    if (!user) return;

    const normalized = normalizeStudioScopeForUser(studioId, user, serverCurrentStudioId);

    if (normalized === studioId) return;

    applyStudioScope(normalized, { syncServer: true });

  }, [user, studioId, serverCurrentStudioId, applyStudioScope]);



  // One-time sync of server current studio (from GET /studios) on load

  useEffect(() => {

    if (!user || serverCurrentStudioId === null || hasSyncedServerRef.current) return;

    hasSyncedServerRef.current = true;

    const stored = readStoredStudioId();

    if (stored === "all" && canUseAllStudiosScope(user)) {

      applyStudioScope("all", { persist: true });

      return;

    }

    if (stored === "all") {

      return;

    }

    const id = serverCurrentStudioId ?? stored;

    applyStudioScope(id, { persist: true });

  }, [user, serverCurrentStudioId, applyStudioScope]);



  const activeStudio = (user?.studios ?? []).find((s) => s.id === studioId) ?? null;



  const handleSetStudio = useCallback(

    (id: string) => {

      const requested = id === "all" && !canUseAllStudiosScope(user) ? (user?.studios?.[0]?.id ?? id) : id;

      const previousId = studioIdRef.current;

      applyStudioScope(requested);



      void apiClient<{ currentStudioId: string }>("/users/me/studio-context", {

        method: "PUT",

        body: JSON.stringify({ studioId: requested }),

        skipAutoRedirect: true,

      } as Parameters<typeof apiClient>[1]).then((res) => {

        if (res.success) return;

        const code = res.error?.code;

        if (requested === "all" && isStudioContextApiErrorCode(code)) {

          const fallback = user?.studios?.[0]?.id;

          if (fallback) {

            applyStudioScope(fallback, { persist: true });

            toast.error(t("studio.scopeAllStudiosRejected"));

          }

          return;

        }

        applyStudioScope(previousId, { persist: true });

        toast.error(getApiErrorMessage(res, t("studio.scopeSwitchFailed")));

      });

    },

    [user, applyStudioScope, t],

  );



  return (

    <StudioContext.Provider

      value={{

        activeStudio,

        activeStudioId: studioId,

        isAllStudios: studioId === "all" && canUseAllStudiosScope(user),

        setActiveStudioId: handleSetStudio,

      }}

    >

      {children}

    </StudioContext.Provider>

  );

}



export function useStudio() {

  const ctx = useContext(StudioContext);

  if (!ctx) throw new Error("useStudio must be used within StudioProvider");

  return ctx;

}

